import { gql, useMutation } from '@apollo/client'
import { useToast } from '@farol-ds/react'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  FEATURE_CLASS,
  GA4FeatureUseEvent,
  REQUIRED_USER_STATUS,
} from '@jusbrasil-web/shared-ga4-events'

import { mapToGA4FeatureName, PartialGA4FeatureUseEventProps } from '../components/ga4-utils'
import {
  UseSaveDocument_DocumentFragment,
  UseSaveDocument_ViewerFragment,
} from './__generated__/use-save-document.graphql'

export const SaveDocumentMutation = gql`
  mutation saveDocument($input: ToggleSaveDocumentInput!) {
    toggleSaveDocument(input: $input) {
      document {
        id
        viewerSaved
      }
    }
  }
`

interface UseSaveDocumentProps {
  document: UseSaveDocument_DocumentFragment
  viewer: UseSaveDocument_ViewerFragment | null
  sendMetrics: (name: string, properties?: Record<string, unknown>) => void
  ga4EventData?: PartialGA4FeatureUseEventProps
}

export type UseSaveDocumentReturn = {
  loading: boolean
  toggleSave: () => Promise<void>
}

export const useSaveDocument = (props: UseSaveDocumentProps): UseSaveDocumentReturn => {
  const { document, viewer, sendMetrics, ga4EventData } = props
  const { docId, artifact, viewerSaved, boxResourceId } = document

  const [mutate, { loading }] = useMutation(SaveDocumentMutation, {
    update(cache, { data: mutate }) {
      cache.modify({
        id: cache.identify(document),
        fields: {
          viewerSaved() {
            return mutate.toggleSaveDocument.document.viewerSaved
          },
        },
      })
    },
  })

  const { sendGA4Event } = useGA4Dispatcher()
  const toast = useToast()

  const sendSaveAttemptGA4Event = (successfulAttempt: boolean): void => {
    if (!ga4EventData) return

    sendGA4Event(
      new GA4FeatureUseEvent({
        requiredUserStatus: REQUIRED_USER_STATUS.AUTHENTICATED_USER,
        ...ga4EventData,
        featureClass: FEATURE_CLASS.DOCUMENT_SAVE,
        featureName: mapToGA4FeatureName(FEATURE_CLASS.DOCUMENT_SAVE, artifact),
        resourceId: boxResourceId,
        successfulAttempt,
      })
    )
  }

  return {
    async toggleSave() {
      if (!viewer) {
        sendSaveAttemptGA4Event(false)
        return
      }

      const action = viewerSaved ? 'UNSAVE' : 'SAVE'
      const input = { docId, artifact, action }
      await mutate({ variables: { input } })

      if (!viewerSaved) {
        toast('Documento salvo', { id: 'save-document' })
        sendSaveAttemptGA4Event(true)
        sendMetrics('Doc.Saved')
      }
    },
    loading,
  }
}

useSaveDocument.fragments = {
  document: gql`
    fragment UseSaveDocument_document on Document {
      id
      docId
      artifact
      viewerSaved
      boxResourceId
    }
  `,
  viewer: gql`
    fragment UseSaveDocument_viewer on Profile {
      pid
    }
  `,
}
